import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { getTrashedEvents, restoreEvent, type TrashedEvent } from "@/services/api/events";
import { USER_ROUTES } from "@/routes";
import { eventPlanOf, planRank } from "@/utils/purchaseEvent";

interface TrashRestoreHintProps {
  currentLocale: string;
}

/**
 * JUB-157 (approved copy): on a free event's plan card, if a paid event was
 * recently deleted, offer to restore it instead of buying again.
 */
export function TrashRestoreHint({ currentLocale }: TrashRestoreHintProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState<TrashedEvent | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTrashedEvents()
      .then((items) => {
        if (cancelled) return;
        const paid = items
          .filter((event) => event.restorable && planRank(eventPlanOf(event)) > 0)
          .sort((a, b) => planRank(eventPlanOf(b)) - planRank(eventPlanOf(a)));
        setCandidate(paid[0] ?? null);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  if (!candidate) {
    return null;
  }

  const handleRestore = async () => {
    setBusy(true);
    try {
      await restoreEvent(candidate.uuid);
      toast.success(t("events.trash.restored"));
      navigate(USER_ROUTES.event(currentLocale, candidate.uuid));
    } catch {
      toast.error(t("events.trash.restoreError"));
    } finally {
      setBusy(false);
    }
  };

  const plan = eventPlanOf(candidate);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-800 dark:bg-amber-950/20" data-testid="trash-restore-hint">
      <p>{t("purchases.restoreHint", { plan: t(`landing.pricing.${plan}.name`) || plan })}</p>
      <p className="mt-1 font-medium">{candidate.name}</p>
      <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={handleRestore} disabled={busy} data-testid="trash-restore-hint-button">
        {t("purchases.restoreAction")}
      </Button>
    </div>
  );
}
